import React, { FC } from "react"
import { Redirect, Route, Switch, useLocation } from "react-router-dom"
import Home from "components/views/home/Home"
import { Nav } from "components/views/app/nav/Nav"
import Title from "components/views/Title"
import ProviderFinder from "components/views/providerfinder/ProviderFinder"
import SubRegion from "components/views/providerfinder/SubRegion"
import City from "components/views/providerfinder/City"
import WhatsImportant from "components/views/providerfinder/WhatsImportant"
import Transactions from "components/views/reimbursements/Transactions"
import { RequestCardContainer } from "components/containers/requestcard/RequestCardContainer"
import RequestCardSuccess from "components/views/requestcard/RequestCardSuccess"
import AccountSettingsContainer from "../../containers/account/AccountSettingsContainer"
import ResetPasswordContainer from "../../containers/login/ResetPasswordContainer"
import ResetPasswordSuccess from "../login/ResetPasswordSuccess"
import { TalkToCarrotRouter } from "../../talk-to-carrot/TalkToCarrotRouter"
import MerchantDeclineContainer from "../../containers/merchantdecline/MerchantDeclineContainer"
import { ServicesLoginContainer } from "../../containers/login/ServicesLoginContainer"
import { ServicesLogoutContainer } from "../../containers/logout/ServicesLogoutContainer"
import { DraggingStateContainer } from "components/views/app/DraggingStateContainer"
import { ReimbursementsRouter } from "components/views/reimbursements/ReimbursementsRouter"
import { BenefitNotStartedContainer } from "components/views/modal/BenefitNotStarted"
import { PageNotFound } from "components/views/error/PageNotFound"
import { SignatureContainer } from "../../containers/signature/SignatureContainer"
import { SignatureCompletedContainer } from "../../containers/signature/SignatureCompletedContainer"
import { LibraryRouter } from "components/containers/library/LibraryRouter"
import ActivateContainer from "../../containers/activate/ActivateContainer"
import { YourBenefitGuide } from "../../../pages/your-benefit-guide"
import { DynamicContentToggles } from "components/dynamic-content/Toggles"
import { Paths } from "../../../utils/Paths"
import { SupportCenterBaseUri, DefaultLocale } from "../../../utils/SupportCenterLinks"
import { CarrotPlansRouter } from "components/carrot-plans/CarrotPlansRouter"
import LockedDownRoute from "./LockedDownRoute"
import { LockedDownGetHelpPage } from "../../talk-to-carrot/TalkToCarrotLockedDownGetHelp"
import { FleComplete } from "../fle/FleComplete"
import InsuranceDetailsPage from "pages/insurance-details"
import { useFeatureToggle } from "components/context/feature-toggle/FeatureToggleContext"
import CarrotPlanRequiredRedirect from "../../../pages/carrot-plan-required-redirect"
import { getBenefitStarted } from "derivedSelectors"
import { useSelector } from "react-redux"

function useAnchorLinkScrollFix(): void {
  const { pathname, hash, key } = useLocation()
  React.useEffect(() => {
    // Scrolling to anchor links is apparently not supported in react-router
    // Here we can scroll to ID'd elements that match our hash (Ex. #bankdetails will scroll to the element with id: bankdetails)
    // Fix lifted from: https://github.com/remix-run/react-router/issues/394#issuecomment-220221604

    if (hash === "") {
      window.scrollTo(0, 0)
    } else {
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          const id = hash.replace("#", "")
          const element = document.getElementById(id)
          if (element) {
            element.scrollIntoView()
          }
        }, 0)
      })
    }
  }, [pathname, hash, key])
}

function useRedirectOnBenefitNotStarted(): boolean {
  const benefitStarted = useSelector(getBenefitStarted)
  const location = useLocation()
  const onHomePage = React.useMemo(() => location.pathname === "/", [location.pathname])
  const shouldRedirect = React.useMemo(() => !onHomePage && !benefitStarted, [benefitStarted, onHomePage])
  return shouldRedirect
}

const App: FC = () => {
  const { showMultiYearCsd } = useFeatureToggle()
  const shouldRedirect = useRedirectOnBenefitNotStarted()
  useAnchorLinkScrollFix()

  return (
    <Switch>
      {/* Routes here are for authenticated pages that do not have side navigation */}
      {shouldRedirect && <Redirect to="/" />}
      <Route from="/reset-password/:guid" component={ResetPasswordContainer} />
      <Route from="/reset-password-success" component={ResetPasswordSuccess} />
      <Route exact path="/registration/complete" component={FleComplete} />
      <Route path="/carrot-plans" component={CarrotPlansRouter} />
      <Route path="/services/login" component={ServicesLoginContainer} />
      <Route path="/services/logout" component={ServicesLogoutContainer} />
      <Route path="/confirm/:token" component={MerchantDeclineContainer} />
      <Route exact path="/signature" component={SignatureContainer} />
      <Route exact path="/signature/completed" component={SignatureCompletedContainer} />

      <Route
        render={() => (
          <DraggingStateContainer>
            <Title />
            <Nav />
            <DynamicContentToggles />
            <BenefitNotStartedContainer />
            <Switch>
              {/* Routes here are for authenticated pages with side navigation */}
              <Route exact path="/" component={Home} />
              {showMultiYearCsd && <Route exact path="/insurance-details" component={InsuranceDetailsPage} />}
              <Route exact path="/talk-to-carrot/get-your-carrot-plan" component={TalkToCarrotRouter} />
              <LockedDownRoute path="/chat" component={TalkToCarrotRouter} />
              <LockedDownRoute path={Paths.TALK_TO_CARROT} component={TalkToCarrotRouter} />
              <Route path="/get-help" component={LockedDownGetHelpPage} />
              <LockedDownRoute path="/ava-bracelet" component={TalkToCarrotRouter} />
              <Redirect from="/journeywelcome" to="/" />
              <Redirect from="/journey" to="/" />
              <Redirect from="/journey/article/:id" to="/" />
              <LockedDownRoute path="/transactions" component={Transactions} />
              <LockedDownRoute path="/reimbursements" component={ReimbursementsRouter} />
              <Route exact path="/account" component={AccountSettingsContainer} />
              <Redirect from="/library" to="/read" />
              <Route path="/read" component={LibraryRouter} />
              <Redirect from="/about" to="/your-benefit-guide/about-your-benefit" />
              <Redirect from="/eligible-expenses" to="/your-benefit-guide/coverage" />
              <Redirect from="/carrot-rx" to="/your-benefit-guide/coverage/carrot-rx" />
              <LockedDownRoute exact path="/providerfinder" component={ProviderFinder} />
              <LockedDownRoute exact path="/providerfinder/:country/:subRegion" component={SubRegion} />
              <LockedDownRoute exact path="/providerfinder/:country/:subRegion/:city" component={City} />
              <LockedDownRoute exact path="/providerfinder/whatsimportant" component={WhatsImportant} />
              <LockedDownRoute path="/request-card/success" exact component={RequestCardSuccess} />
              <LockedDownRoute path="/request-card" component={RequestCardContainer} />
              <Route path={Paths.YOUR_BENEFIT_GUIDE} component={YourBenefitGuide} />
              <Redirect exact path="/register/:guid" to={"/"} />
              <Redirect exact path="/signin" to={"/"} />
              <Redirect exact path="/signup" to={"/"} />
              <Redirect exact path="/sign-up" to={"/"} />
              <Redirect exact path="/login" to={"/"} />
              <Redirect exact path="/request-reset-password" to={"/"} />
              <LockedDownRoute path="/activate" component={ActivateContainer} />
              <Route path="/carrot-plan-required" component={CarrotPlanRequiredRedirect} />
              <Route
                path="/support"
                component={() => {
                  window.location.replace(`${SupportCenterBaseUri}/${DefaultLocale}`)
                  return null
                }}
              />

              {/* 404 catch all */}
              <Route component={PageNotFound} />
            </Switch>
          </DraggingStateContainer>
        )}
      />
    </Switch>
  )
}

export { App }
